import type { BedCensus, LicensedBeds, RelativityFactors } from "./types";

/**
 * Weighted bed-equivalent count: each census bed-type multiplied by
 * its relativity factor, summed across the 4 buckets defined by the
 * UW pricing directive.
 *
 *   Equivalency = (Skilled     × Skilled Rel)
 *               + (Assisted    × Assisted Rel)
 *               + (Independent × Independent Rel)
 *               + (Addl Exp.   × Addl Rel)
 *
 * Drives the downstream premium chain — Unadj. Premium = Rate ×
 * Equivalency. The factors are external (passed via `relativity`) so
 * a callsite can override them per-submission without monkey-patching
 * the constant.
 */
export function computeEquivalency(census: BedCensus, relativity: RelativityFactors): number {
    return (
        census.skilled * relativity.skilled +
        census.assisted * relativity.assisted +
        census.independent * relativity.independent +
        census.addl * relativity.addl
    );
}

/**
 * Licensed Equivalency — the capacity-side counterpart to
 * `computeEquivalency`. Sums licensed beds weighted by relativity:
 *
 *   Licensed Equivalency = (Lic SNF × Skilled Rel)
 *                        + (Lic ALF × Assisted Rel)
 *                        + (Lic ILF × Independent Rel)
 *
 * Addl Exposure has no licensed-side column — only 3 buckets
 * contribute. Used ONLY as the denominator of Occupancy. The
 * "Total Lic." column in the Schedule is the plain (unweighted)
 * sum of the 3 licensed buckets instead.
 */
export function computeLicensedEquivalency(
    licensed: LicensedBeds,
    relativity: RelativityFactors,
): number {
    return (
        licensed.snf * relativity.skilled +
        licensed.alf * relativity.assisted +
        licensed.ilf * relativity.independent
    );
}
